import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import MyBadge from "../Common/MyBadge";
import MySearch from "../Common/MySearch";
import MyPagination from "../Common/MyPagination";
import WhiteButton from "../Common/WhiteButton";
import MyTemplateCard from "./MyTemplateCard";
import TemplateDetail from "./TemplateDetail";
import EngineerVisitIcon from "../../assets/icons/engineer.svg2";
import TickIcon from "../../assets/icons/tick.svg2";
import ApiProfilesLibraryIcon from "../../assets/icons/icon_api_endpoints-01.svg2";
import DataImportModal from "../Common/DataImportModal/DataImportModal";
import Subheading from "../Common/Subheading";
import { useGetApiConnectionsQuery } from "../store/slices/settingsSlice";
import ImportIcon from "../../assets/icons/import-01.svg2";
import HelpButton from "../HelpContent/HelpButton";

export default function ApiConnectionLibrary({
  isModal,
}: {
  isModal?: boolean;
}) {
  const [selectedTemplateUUID, setSelectedTemplateUUID] = useState<string>();
  const { connectionUUID, selectedTabName } = useParams<{
    connectionUUID: string;
    selectedTabName: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [orderBy, setOrderBy] = useState("used-last");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(12);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const [newConnection, setNewConnection] = useState({
    Label: "New API Connection",
    Reference: "",
    Description: "",
    Kind: "",
    Status: "production",
    NylasEmail: "",
  });

  const {
    data: { connections: availableTemplates, total: totalResults } = {
      connections: [],
      total: 0,
    },
  } = useGetApiConnectionsQuery({
    status,
    kind,
    orderBy,
    search: debouncedSearch,
    offset,
    limit,
  });

  const editedTemplate =
    connectionUUID === "add"
      ? newConnection
      : availableTemplates?.find(
          (template) => template.Uuid === connectionUUID
        );

  const selectedTemplate = availableTemplates?.find(
    (template) => template.Uuid === selectedTemplateUUID
  );

  useEffect(() => {
    if (availableTemplates?.length > 0 && !selectedTemplate) {
      setSelectedTemplateUUID(availableTemplates[0].Uuid);
    }
  }, [availableTemplates]);

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <Subheading
            Icon={ApiProfilesLibraryIcon}
            heading="API Connections"
            subheading="Access your API connections."
          />
        </div>
        <div className="d-flex gap-2 align-items-center">
          <WhiteButton
            as="RRLink"
            to="/library/api-connections/add/api-type"
            icon={ImportIcon}
            heading="Connect"
            subheading="New API"
            disabled={false}
            target={undefined}
          />
        </div>
      </div>
      <div className="mb-3 bg-colored-reverse p-3 rounded-3 d-flex justify-content-between gap-3 align-items-end">
        <div>
          <label className="fw-bold p-2 text-white">Order by:</label>

          <Form.Select
            aria-label="newTemplate-select"
            onChange={(e) => {
              setOrderBy(e.target.value);
            }}
            value={orderBy}
          >
            <option value="created-last">Created Last</option>
            <option value="created-first">Created First</option>
            <option value="updated-first">Updated First</option>
            <option value="updated-last">Updated Last</option>
            <option value="used-last">Used Last</option>
            <option value="used-first">Used First</option>
          </Form.Select>
        </div>
        <div>
          <label className="fw-bold p-2 text-white">Status:</label>
          <Input
            value={[status]}
            onChange={(v: any) => {
              setStatus(v[0]);
            }}
            inputType="radio"
            radioName="status"
            options={[
              { value: "draft", label: "DRAFT" },
              { value: "production", label: "PRODUCTION" },
              { value: "archived", label: "ARCHIVED" },
              { value: "", label: "ALL" },
            ]}
          />
        </div>
        <div>
          <label className="fw-bold p-2 text-white">Kind:</label>
          <Input
            value={[kind]}
            onChange={(v: any) => {
              setKind(v[0]);
            }}
            inputType="radio"
            radioName="kind"
            options={[
              { value: "nylas", label: "NYLAS" },
              { value: "third-party", label: "THIRD PARTY" },
              { value: "logic", label: "LOGIC" },
              { value: "messaging", label: "MESSAGING" },
              { value: "", label: "ALL" },
            ]}
          />
        </div>
        <div className="flex-grow-1"></div>

        <div>
          <label htmlFor=""></label>
          <MySearch
            placeholder="Search for a template"
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>
      <Row className="mb-5 my-3 w-100">
        <Col xs={12} lg={4} className="fw-bold pe-5 border-end border-2">
          <div
            style={{
              maxHeight: 500,
              overflowY: "auto",
              scrollbarColor: "purple",
            }}
            className="pe-2 fw-bold"
          >
            <span className="fs-3">
              {" "}
              Template Results <HelpButton helpKey="template-results" />
            </span>
            <MyBadge count={totalResults} />
            {(availableTemplates || []).map((template) => (
              <div
                key={template.Uuid}
                onDoubleClick={() => {
                  setSelectedTemplateUUID(template.Uuid);
                  navigate(
                    `/library/api-connections/${template.Uuid}/details`
                  );
                }}
              >
                <MyTemplateCard
                  isSelected={selectedTemplate === template}
                  onSelect={() => setSelectedTemplateUUID(template.Uuid)}
                >
                  <div>
                    {template.Label} (#{template.Reference})
                  </div>
                  <div>
                    Kind: {template.Kind}, Status: {template.Status}
                  </div>
                </MyTemplateCard>
              </div>
            ))}
          </div>
          <MyPagination
            key={debouncedSearch}
            total={totalResults}
            onPaginate={(page: number, perPage: number) => {
              setLimit(perPage);
              setOffset(page * perPage);
            }}
          />
        </Col>
        <div className="col-lg-1"></div>
        <Col className="fw-bold">
          {selectedTemplate ? (
            <>
              About this template:
              <div className="my-3">
                <div
                  className={`btn w-75 h-100 px-0  text-start rounded-3 position-relative `}
                >
                  <div className="d-flex align-items-center">
                    <div className="bg-purple-gradient rounded-circle me-3">
                      <EngineerVisitIcon width={60} />
                    </div>
                    <div className="me-3">
                      <h3 className="text-black fw-bold">
                        {selectedTemplate.Label}
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="fw-bold my-4">
                  This template will setup the following for you:
                </div>
                <Row>
                  <div className="col-6">
                    <TemplateDetail
                      label="Label"
                      value={selectedTemplate.Label || "No label defined"}
                      Icon={undefined}
                    />
                  </div>
                  <div className="col-6">
                    <TemplateDetail
                      label="Kind"
                      value={selectedTemplate.Kind}
                      Icon={undefined}
                    />
                  </div>

                  <div className="col-6">
                    <TemplateDetail
                      label="Description"
                      value={selectedTemplate.Description}
                      Icon={undefined}
                    />
                  </div>
                  <div className="col-6">
                    <TemplateDetail
                      label="Reference"
                      value={selectedTemplate.Reference}
                      Icon={undefined}
                    />
                  </div>
                  <div className="col-6">
                    <TemplateDetail
                      label="Status"
                      value={selectedTemplate.Status}
                      Icon={undefined}
                    />
                  </div>
                  {selectedTemplate.Kind === "nylas" && (
                    <div className="col-6">
                      <TemplateDetail
                        label="Nylas Email"
                        value={
                          selectedTemplate.NylasEmail || "No email defined"
                        }
                        Icon={undefined}
                      />
                    </div>
                  )}
                  {selectedTemplate.Kind === "third-party" &&
                    selectedTemplate.Profile?.BaseUrl && (
                      <div className="col-6">
                        <TemplateDetail
                          label="Base URL"
                          value={selectedTemplate.Profile?.BaseUrl}
                          Icon={undefined}
                        />
                      </div>
                    )}
                  {selectedTemplate.Kind === "logic" && (
                    <>
                      {selectedTemplate.LastUsed && (
                        <div className="col-6">
                          <TemplateDetail
                            label="Last Used"
                            value={new Date(
                              selectedTemplate.LastUsed + "Z"
                            ).toLocaleString()}
                            Icon={undefined}
                          />
                        </div>
                      )}
                      {selectedTemplate.Triggers?.length && (
                        <div className="col-6">
                          <TemplateDetail
                            label="Triggers"
                            value={
                              <ul>
                                {selectedTemplate.Triggers?.map((t) => (
                                  <li key={t.Project?.ID}>
                                    {t.Project?.Label} (
                                    <Link
                                      to={`/library/logic/builder/${t.Project?.ID}`}
                                      target="_blank"
                                    >
                                      #{t.Reference}
                                    </Link>
                                    )
                                  </li>
                                )) || "No triggers defined"}
                              </ul>
                            }
                            Icon={undefined}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Row>
              </div>
              <div className="d-flex justify-content-start my-5">
                <Link
                  to={`/library/api-connections/${selectedTemplate.Uuid}/details`}
                  className="btn bg-colored-reverse rounded-pill"
                >
                  <div className="d-flex align-items-center ">
                    <TickIcon
                      width={40}
                      className=" white rounded-circle p-1"
                    />
                    <h5 className="my-0 mx-3 text-white">Edit</h5>
                  </div>
                </Link>
              </div>
            </>
          ) : (
            <div className="p-5 text-center fs-2">Nothing is selected.</div>
          )}
        </Col>
      </Row>
      {connectionUUID && (
        <DataImportModal
          apiUuid={connectionUUID}
          initialTabName={selectedTabName}
          onClose={() => {
            if (location.state?.returnLocation) {
              navigate(location.state.returnLocation.pathname, {
                state: location.state.returnLocation.state,
              });
              return;
            }
            if (!!location.state?.previousLocationPathname) {
              navigate(-1);
              return;
            }
            navigate("/library/api-connections");
          }}
        />
      )}
    </div>
  );
}
